import { useState, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { FaTimes, FaBars } from "react-icons/fa";
import brandLogo from "../assets/brandLogo.png";

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const [activeSection, setActiveSection] = useState<string>("");
  const isApplyNowPage = location.pathname === "/apply-now";

  // Navigation links for both desktop and mobile
  const navLinks = [
    { label: "Home", href: "/" },
    { label: "Loan Calculator", href: "/#loan-calculator" },
    { label: "About Us", href: "/about" },
    { label: "Contact", href: "/contact" },
    // { label: "FAQs", href: "/faq" }
  ];

  useEffect(() => {
    if (location.pathname !== "/") {
      setActiveSection("");
      return;
    }

    // Set active section on initial load or click if hash is present
    if (location.hash) {
      const hashId = location.hash.replace("#", "");
      setActiveSection(hashId);
      return;
    }

    const sections = ["loan-calculator", "how-it-works"];
    const observerOptions = {
      root: null,
      rootMargin: "-30% 0px -40% 0px", // Trigger when section occupies center of viewport
      threshold: 0
    };

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    sections.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    const handleScroll = () => {
      // If close to the top, reset active section to "Loans"
      if (window.scrollY < 180) {
        setActiveSection("");
      }
    };
    window.addEventListener("scroll", handleScroll);

    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", handleScroll);
    };
  }, [location.pathname, location.hash]);

  const isLinkActive = (href: string) => {
    if (location.pathname === "/") {
      if (href === "/") return activeSection === "";
      if (href === "/#how-it-works") return activeSection === "how-it-works";
      if (href === "/#loan-calculator") return activeSection === "loan-calculator";
      return false;
    }
    return location.pathname === href;
  };

  return (
    <nav className="w-full fixed top-0 left-0 z-[100]" style={{background: '#FDF0ED'}}>
      <div className="max-w-[1500px] mx-auto px-6 xl:px-12 2xl:px-20 py-4 flex items-center justify-between relative">
        {/* Logo and Brand */}
        <a href="/" className="flex items-center gap-3 min-w-[180px]">
          <img
            className="h-11 w-auto object-contain"
            src={brandLogo}
            alt={import.meta.env.VITE_COMPANY_NAME}
          />
        </a>

        {/* Desktop Navigation - Centered */}
        <div className="hidden xl:flex items-center xl:gap-6 2xl:gap-10 absolute left-1/2 -translate-x-1/2">
          {navLinks.map((item) => {
            const active = isLinkActive(item.href);
            return (
              <a
                key={item.label}
                href={item.href}
                className={`relative font-semibold text-[16px] px-1 pb-1 transition-colors ${active ? 'text-[#C03221]' : 'text-[#334155] hover:text-[#C03221]'}`}
                style={{ fontFamily: 'inherit' }}
              >
                {item.label}
                {active && (
                  <span className="absolute bottom-[-6px] left-0 right-0 h-[3px] bg-[#C03221] rounded" />
                )}
              </a>
            );
          })}
        </div>

        {/* Desktop Right Action Buttons */}
        <div className="hidden xl:flex items-center gap-3">
          <a
            href={`${import.meta.env.VITE_WEBSITE_LINK}/login`}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-transparent border-2 border-[#C03221] hover:bg-[#C03221] text-[#C03221] hover:text-white px-7 py-2.5 rounded-[12px] font-bold text-[15px] transition-all active:scale-95 text-center"
            style={{ minWidth: 110 }}
          >
            Login
          </a>
          <a
            href={`${import.meta.env.VITE_WEBSITE_LINK}/repay-now`}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-transparent border-2 border-[#C03221] hover:bg-[#C03221] text-[#C03221] hover:text-white px-7 py-2.5 rounded-[12px] font-bold text-[15px] transition-all active:scale-95 text-center"
            style={{ minWidth: 130 }}
          >
            Repay Now
          </a>
          {!isApplyNowPage && (
            <Link
              to="/apply-now"
              className="bg-[#C03221] hover:bg-[#A82A1B] text-white px-7 py-2.5 rounded-[12px] font-bold text-[15px] transition-all active:scale-95 text-center"
              style={{ minWidth: 130 }}
            >
              Apply Now
            </Link>
          )}
        </div>

        {/* Mobile Menu Trigger */}
        <button
          className="xl:hidden relative z-[101] text-[#111] p-2 -mr-2"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle Menu"
        >
          {menuOpen ? <FaTimes className="text-2xl" /> : <FaBars className="text-2xl" />}
        </button>

        {/* Mobile Drawer */}
        <div
          className={`fixed inset-0 top-0 left-0 w-full h-screen bg-[#FDF0ED] z-[100] p-6 pt-24 flex flex-col gap-2 xl:hidden transition-all duration-500 ease-in-out ${
            menuOpen ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
          }`}
        >
          <div className="flex flex-col gap-2">
            {navLinks.map((item) => {
              const active = isLinkActive(item.href);
              return (
                <a
                  key={item.label}
                  href={item.href}
                  className={`w-full font-bold text-xl py-4 border-b border-[var(--border-light)] flex items-center justify-between group ${active ? 'text-[#C03221]' : 'text-[#111] hover:text-[#C03221]'}`}
                  onClick={() => setMenuOpen(false)}
                >
                  {item.label}
                  {active && (
                    <span className="block h-[3px] w-8 bg-[#C03221] rounded ml-2" />
                  )}
                </a>
              );
            })}
          </div>

          <div className="mt-auto pb-10 flex flex-col gap-3">
            <a
              href={`${import.meta.env.VITE_WEBSITE_LINK}/login`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full inline-flex items-center justify-center py-5 rounded-xl bg-transparent border-2 border-[#C03221] text-[#C03221] hover:bg-[#C03221] hover:text-white font-bold text-xl active:scale-95 transition-all"
              style={{ minWidth: 140 }}
            >
              Login
            </a>
            <a
              href={`${import.meta.env.VITE_WEBSITE_LINK}/repay-now`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full inline-flex items-center justify-center py-5 rounded-xl bg-transparent border-2 border-[#C03221] text-[#C03221] hover:bg-[#C03221] hover:text-white font-bold text-xl active:scale-95 transition-all"
              style={{ minWidth: 140 }}
            >
              Repay Now
            </a>
            {!isApplyNowPage && (
              <Link
                to="/apply-now"
                onClick={() => setMenuOpen(false)}
                className="w-full inline-flex items-center justify-center py-5 rounded-xl bg-[#C03221] text-white hover:bg-[#A82A1B] font-bold text-xl active:scale-95 transition-all"
                style={{ minWidth: 140 }}
              >
                Apply Now
              </Link>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
